import React from 'react';
import { useSelector } from 'react-redux';
import FormNavigation from 'components/FormNavigation';
import FormContainer from 'components/LoanForm/FormContainer';
import Button from 'components/Button';
import { ButtonType } from 'components/Button/Button';
import StudentLoanLoaderWrapper from 'components/StudentLoanForgiveness/LoaderWrapper';
import {
  Callout,
  Checklist,
  InfoNote,
  PaymentSummary,
  SavingsHeadline,
} from 'components/StudentLoanForgiveness/Common/ResultBlocks';
import { getFindingCopy } from 'components/StudentLoanForgiveness/studentLoanCopy';
import {
  formatPayment,
  getPathSummaryRows,
  getSavingsLabel,
} from 'components/StudentLoanForgiveness/studentLoanFormat';
import { ResultBasis } from 'api/StudentLoanApi';
import { EstimateScreenResult } from 'enums/StudentLoanForgivenessFlowResults';
import { FlowComponentType } from 'routes/types';
import { getStudentLoanData, getStudentLoanPlanResult } from 'selectors/getStudentLoanData';
import { ReactComponent as StudentLoanIcon } from 'images/student-loan.svg';

import commonStyles from 'components/StudentLoanForgiveness/Common/Common.module.scss';

const getEstimateIntro = (basis: ResultBasis) =>
  basis === ResultBasis.StaleFile
    ? {
        subtitle: 'Based on your older studentaid.gov file and your answers.',
        calloutTitle: 'Turn this range into a plan.',
        calloutBody: 'Upload a fresh studentaid.gov file. It takes about 3 minutes.',
      }
    : {
        subtitle: 'Based on your credit report and your answers.',
        calloutTitle: 'Refine your estimate and get your steps.',
        calloutBody: 'Upload your studentaid.gov file. It takes about 3 minutes.',
      };

const Estimate = ({ navigationInfo, handleNext }: FlowComponentType) => {
  const planResult = getStudentLoanPlanResult(useSelector(getStudentLoanData));
  const savingsLabel = planResult ? getSavingsLabel(planResult) : null;
  const intro = getEstimateIntro(planResult?.basis ?? ResultBasis.CreditReport);

  return (
    <StudentLoanLoaderWrapper>
      <>
        <FormNavigation {...navigationInfo} />
        {planResult && (
          <FormContainer
            icon={<StudentLoanIcon />}
            title={savingsLabel ? 'Your estimated savings' : "You're on a good path"}
            subtitle={intro.subtitle}
          >
            {savingsLabel && <SavingsHeadline amount={savingsLabel} />}

            <PaymentSummary
              amount={formatPayment(planResult.recommended.firstMonthlyPayment)}
              rows={getPathSummaryRows(planResult.recommended)}
            />

            {planResult.findings.map((finding) => (
              <InfoNote key={finding.code} {...getFindingCopy(finding, { basis: planResult.basis })} />
            ))}

            <Callout>
              <b>{intro.calloutTitle}</b> {intro.calloutBody}
            </Callout>

            {planResult.fileWillConfirm.length > 0 && (
              <Checklist title="Your file will confirm" items={planResult.fileWillConfirm} />
            )}

            <div className={commonStyles.buttons}>
              <Button onClick={() => handleNext(EstimateScreenResult.Refine)}>Refine my estimate</Button>
              <Button type={ButtonType.Transparent} onClick={() => handleNext(EstimateScreenResult.NotNow)}>
                Not now
              </Button>
            </div>
          </FormContainer>
        )}
      </>
    </StudentLoanLoaderWrapper>
  );
};

export default Estimate;
